import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

interface QuillWYSIWYGProps {
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    height?: string;
    disabled?: boolean;
}

const QuillWYSIWYG = forwardRef<any, QuillWYSIWYGProps>(({ 
    value = "", 
    onChange, 
    placeholder = "Enter your content...",
    height = "200px",
    disabled = false
}, ref) => {
    const quillRef = useRef<HTMLDivElement>(null);
    const quillInstance = useRef<Quill | null>(null);

    useImperativeHandle(ref, () => ({
        focus: () => {
            if (quillInstance.current) {
                quillInstance.current.focus();
            }
        }
    }));

    useEffect(() => {
        if (quillRef.current && !quillInstance.current) {
            // Initialize Quill
            quillInstance.current = new Quill(quillRef.current, {
                theme: 'snow',
                placeholder: placeholder,
                modules: {
                    toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'color': [] }, { 'background': [] }],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        [{ 'align': [] }],
                        ['link'],
                        ['clean']
                    ],
                },
                readOnly: disabled,
            });

            // Set initial content
            if (value) {
                quillInstance.current.root.innerHTML = value;
            }

            // Handle content changes
            quillInstance.current.on('text-change', () => {
                if (quillInstance.current && onChange) {
                    const html = quillInstance.current.root.innerHTML;
                    onChange(html);
                }
            });
        }

        return () => {
            if (quillInstance.current) {
                quillInstance.current = null;
            }
        };
    }, []);

    // Update content when value prop changes
    useEffect(() => {
        if (quillInstance.current && value !== undefined) {
            const currentContent = quillInstance.current.root.innerHTML;
            if (currentContent !== value) {
                quillInstance.current.root.innerHTML = value;
            }
        }
    }, [value]);

    // Update disabled state
    useEffect(() => {
        if (quillInstance.current) {
            quillInstance.current.enable(!disabled);
        }
    }, [disabled]);

    return (
        <div style={{ 
            minHeight: height,
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            backgroundColor: disabled ? '#f5f5f5' : '#fff'
        }}>
            <div 
                ref={quillRef} 
                style={{ 
                    minHeight: `calc(${height} - 42px)`, // Subtract toolbar height
                }}
            />
        </div>
    );
});

QuillWYSIWYG.displayName = 'QuillWYSIWYG';

export default QuillWYSIWYG;
